import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  TextField,
  alpha,
} from "@mui/material";
import { AccessTime as AccessTimeIcon } from "@mui/icons-material";
import { dayNames, formatTime } from "../utils/storeDetailsHelpers";
import { OpeningHourDto } from "@swagger/typescript-fetch-goflyer";
import { FieldPath, FieldValue } from "../utils/fieldChangeHelpers";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";

interface OperatingHoursProps {
  openingHours: OpeningHourDto[];
  isEditing: boolean;
  onFieldChange: (field: FieldPath, value: FieldValue) => void;
}

export const OperatingHours: React.FC<OperatingHoursProps> = ({
  openingHours,
  isEditing,
  onFieldChange,
}) => {
  const { t } = useTranslation();

  return (
    <Card
      elevation={1}
      sx={{
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        border: "1px solid",
        borderColor: alpha("#000", 0.06),
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <AccessTimeIcon sx={{ color: "#f58321", mr: 1 }} />
          <Typography variant="h6" fontWeight={600}>
            {t(messages.Operating_Hours())}
          </Typography>
        </Box>

        {isEditing ? (
          <Grid container spacing={2}>
            {dayNames.map((dayName, dayIndex) => {
              const hoursObj =
                Array.isArray(openingHours) &&
                openingHours[dayIndex] &&
                typeof openingHours[dayIndex] === "object"
                  ? openingHours[dayIndex]
                  : {
                      open: { day: dayIndex, time: "0900" },
                      close: { day: dayIndex, time: "1700" },
                    };

              return (
                <Grid item xs={12} key={dayName}>
                  <Box
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: alpha("#02b7cf", 0.2),
                      borderRadius: 2,
                      backgroundColor: alpha("#02b7cf", 0.05),
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      color="#02b7cf"
                      mb={1}
                    >
                      {dayName}
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={5}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t(messages.Open_Time())}
                          placeholder="0900"
                          value={(hoursObj as any)?.open?.time || "0900"}
                          onChange={(e) =>
                            onFieldChange(
                              `Hours_${dayIndex}_open`,
                              e.target.value
                            )
                          }
                          variant="outlined"
                          inputProps={{ maxLength: 4 }}
                          helperText={t(messages.Time_Format_AM_Helper())}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2} sx={{ textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          {t(messages.To())}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t(messages.Close_Time())}
                          placeholder="1700"
                          value={(hoursObj as any)?.close?.time || "1700"}
                          onChange={(e) =>
                            onFieldChange(
                              `Hours_${dayIndex}_close`,
                              e.target.value
                            )
                          }
                          variant="outlined"
                          inputProps={{ maxLength: 4 }}
                          helperText={t(messages.Time_Format_PM_Helper())}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <>
            {openingHours && openingHours.length > 0 ? (
              <Grid container spacing={2}>
                {openingHours.map((hours, index) => {
                  if (typeof hours === "string") {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Box
                          sx={{
                            p: 2,
                            border: "1px solid",
                            borderColor: alpha("#02b7cf", 0.2),
                            borderRadius: 2,
                            backgroundColor: alpha("#02b7cf", 0.05),
                          }}
                        >
                          <Typography variant="body2">{hours}</Typography>
                        </Box>
                      </Grid>
                    );
                  }

                  const hoursObj = hours as any;
                  if (hoursObj?.open && hoursObj?.close) {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Box
                          sx={{
                            p: 2,
                            border: "1px solid",
                            borderColor: alpha("#02b7cf", 0.2),
                            borderRadius: 2,
                            backgroundColor: alpha("#02b7cf", 0.05),
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            color="#02b7cf"
                            mb={1}
                          >
                            {dayNames[hoursObj.open.day]}
                          </Typography>
                          <Typography variant="body2">
                            {formatTime(hoursObj.open.time)} -{" "}
                            {formatTime(hoursObj.close.time)}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  }

                  return null;
                })}
              </Grid>
            ) : (
              <Typography color="text.secondary">
                {t(messages.Operating_Hours_Not_Available())}
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
